import { getUserInfo } from '@marriott/mi-store-utils';
import { useEffect, useRef } from 'react';
import { getWindowSession, isAuthenticated, SessionMemStatePayload } from '../helper';
import { useIsFeatureFlagActive } from './useIsFeatureFlagActive';

export interface UseGetUserInfoOptions {
  requestId: string | undefined;
  currentLocale: string | undefined;
}

/**
 * Calls getUserInfo once per mount as soon as a session token is available.
 * A ref guard prevents duplicate calls even when sessionData, requestId, or
 * currentLocale change after the initial invocation (e.g. after a client-side
 * session refresh), which avoids infinite re-render loops.
 *
 * This hook fetches user details from /mi/phoenix-account-auth/v2/userDetails
 * and stores them in userDetailsGlobalStore for use across the application.
 */
export function useGetUserInfo({ requestId, currentLocale }: UseGetUserInfoOptions): void {
  const calledRef = useRef(false);
  const windowSession = getWindowSession();
  const sessionToken =
    windowSession?.sessionToken ??
    windowSession?.data?.sessionToken ??
    windowSession?.cacheData?.key ??
    windowSession?.cacheData?.data?.sessionToken;
  const authenticated = isAuthenticated(windowSession as SessionMemStatePayload);
  const isConcurFlowEnabled = useIsFeatureFlagActive('experience_mcom_book_enable_concur_flow');

  const enabled = isConcurFlowEnabled && authenticated && sessionToken;

  useEffect(() => {
    if (!enabled || calledRef.current) return;
    calledRef.current = true;
    const userId = document?.cookie?.split('UserIdToken=')[1]?.split(';')[0];
    getUserInfo(false, {
      'x-request-id': requestId,
      'accept-language': currentLocale || '',
      Cookie: `sessionID=${sessionToken};UserIdToken=${userId || ''};`,
    });
  }, [requestId, currentLocale, enabled, sessionToken]);
}
